import { Directive, ElementRef, HostListener, Input, Optional } from '@angular/core';
import { NgControl } from '@angular/forms';

/**
* Accepts only English decimal numbers; converts Persian/Arabic numbers to English,
* Keeps only one decimal point and limits the number of decimal digits if needed.
* Usage:
*   <input appDecimalOnly /> -> Unlimited decimals
*   <input [appDecimalOnly]="1" /> -> Maximum 1 decimal digit
*   <input [appDecimalOnly]="2" /> -> Maximum 2 decimal digits
*/
@Directive({
  selector: '[appDecimalOnly]',
})
export class DecimalOnlyDirective {
  @Input('appDecimalOnly') precision: number | string | null = null;

  constructor(private el: ElementRef, @Optional() private control: NgControl) {}

  private faToEn(value: string): string {
    return value.replace(/[\u0660-\u0669\u06f0-\u06f9]/g, (c) =>
      (c.charCodeAt(0) & 0xf).toString(),
    );
  }

  private sanitize(raw: string): string {
    let v = this.faToEn(raw ?? '').replace(/[^0-9.]/g, '');
    if (!v) return '';
    // Only one decimal point
    const firstDot = v.indexOf('.');
    if (firstDot !== -1) {
      v =
        v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, '');
    }
    // single point -> 0.
    if (v === '.') return '0.';
    // First point -> 0.x
    if (v.startsWith('.')) v = '0' + v;
    // Decimal digit limit
    const p =
      this.precision === null || this.precision === ''
        ? null
        : Number(this.precision);
    if (p !== null && Number.isFinite(p) && (p as number) >= 0 && v.includes('.')) {
      const [int, dec = ''] = v.split('.');
      v = int + '.' + dec.slice(0, p as number);
    }
    return v;
  }

  private apply(value: string): void {
    const input = this.el.nativeElement as HTMLInputElement;
    if (input.value !== value) input.value = value;
    if (this.control?.control && this.control.control.value !== value) {
      this.control.control.setValue(value, { emitEvent: false });
    }
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const sanitized = this.sanitize(input?.value ?? '');
    if (input && input.value !== sanitized) {
      // Maintain approximate cursor position
      const pos = input.selectionStart ?? sanitized.length;
      const diff = input.value.length - sanitized.length;
      this.apply(sanitized);
      try {
        input.setSelectionRange(Math.max(0, pos - diff), Math.max(0, pos - diff));
      } catch {}
    } else {
      this.apply(sanitized);
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): boolean {
    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'Home', 'End', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
    ];
    if (allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey) return true;
    if (/^[0-9]$/.test(event.key)) return true;
    if (event.key === '.' || event.key === '٫') {
      const input = this.el.nativeElement as HTMLInputElement;
      if (input.value.includes('.')) {
        event.preventDefault();
        return false;
      }
      return true;
    }
    event.preventDefault();
    return false;
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') ?? '';
    const input = this.el.nativeElement as HTMLInputElement;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    const next = input.value.slice(0, start) + pasted + input.value.slice(end);
    this.apply(this.sanitize(next));
  }
}
